import { useMemo } from 'react';

import type { HomeWidgetsPriority } from '../../../components/widgets/homeWidgets/types';
import { useFlag } from '../../../services/launchDarkly';
import type { Widget, WidgetEntry, WidgetSizeType } from '../types';
import { sortWidgets } from '../utils';

import { useBingoWidgets } from './useBingoWidgets';
import { useCashOfferWidgets } from './useCashOfferWidgets';
import { useChallengeWidgets } from './useChallengeWidgets';
import { useChampionsTournamentWidgets } from './useChampionsTournamentWidgets';
import { useCoinBackWidgets } from './useCoinBackWidgets';
import { useCrownRipsWidgets } from './useCrownRipsWidgets';
import { useEventTriggerWidgets } from './useEventTriggerWidgets';
import { useFreeSpinWidgets } from './useFreeSpinWidgets';
import { useJackpotWidgets } from './useJackpotWidgets';
import { useMetagameWidgets } from './useMetagameWidgets';
import { useMiniGameWidgets } from './useMiniGameWidgets';
import { useMonetizationWidgets } from './useMonetizationWidgets';
import { usePrizeDropWidgets } from './usePrizeDropWidgets';
import { useQuestWidgets } from './useQuestWidgets';
import { useSlotBattleWidgets } from './useSlotBattleWidgets';
import { useSnappyGiftWidgets } from './useSnappyGiftWidgets';
import { useStampCardWidgets } from './useStampCardWidgets';
import { useTournamentWidgets } from './useTournamentWidgets';
import { useUpcomingDailyMissionWidgets } from './useUpcomingDailyMissionWidgets';
import { useUpcomingMissionWidgets } from './useUpcomingMissionWidgets';
import { useWidgetLayout } from './useWidgetLayout';

export function useWidgets() {
  const homeWidgetsOrder = useFlag<HomeWidgetsPriority | undefined>('home_widgets_order', undefined);
  const bingoWidgets = useBingoWidgets();
  const cashOfferWidgets = useCashOfferWidgets();
  const challengeWidgets = useChallengeWidgets();
  const eventTriggerWidgets = useEventTriggerWidgets();
  const metagameWidgets = useMetagameWidgets();
  const snappyGiftWidgets = useSnappyGiftWidgets();
  const questWidgets = useQuestWidgets();
  const stampCardWidgets = useStampCardWidgets();
  const freeSpinWidgets = useFreeSpinWidgets();
  const miniGameWidgets = useMiniGameWidgets();
  const coinBackWidgets = useCoinBackWidgets();
  const prizeDropWidgets = usePrizeDropWidgets();
  const tournamentWidgets = useTournamentWidgets();
  const championsTournamentWidgets = useChampionsTournamentWidgets();
  const upcomingDailyMissionWidgets = useUpcomingDailyMissionWidgets();
  const monetizationWidgets = useMonetizationWidgets();
  const upcomingMissionWidgets = useUpcomingMissionWidgets();
  const crownRipsWidgets = useCrownRipsWidgets();
  const jackpotWidgets = useJackpotWidgets();
  const slotBattleWidgets = useSlotBattleWidgets();

  const widgetsData: WidgetEntry[] = useMemo(
    () => [
      ...bingoWidgets,
      ...cashOfferWidgets,
      ...challengeWidgets,
      ...championsTournamentWidgets,
      ...coinBackWidgets,
      ...eventTriggerWidgets,
      ...freeSpinWidgets,
      ...miniGameWidgets,
      ...metagameWidgets,
      ...prizeDropWidgets,
      ...questWidgets,
      ...snappyGiftWidgets,
      ...stampCardWidgets,
      ...tournamentWidgets,
      ...upcomingDailyMissionWidgets,
      ...upcomingMissionWidgets,
      ...monetizationWidgets,
      ...crownRipsWidgets,
      ...jackpotWidgets,
      ...slotBattleWidgets,
    ],
    [
      bingoWidgets,
      cashOfferWidgets,
      challengeWidgets,
      championsTournamentWidgets,
      coinBackWidgets,
      eventTriggerWidgets,
      freeSpinWidgets,
      metagameWidgets,
      miniGameWidgets,
      monetizationWidgets,
      prizeDropWidgets,
      questWidgets,
      snappyGiftWidgets,
      stampCardWidgets,
      tournamentWidgets,
      upcomingDailyMissionWidgets,
      upcomingMissionWidgets,
      crownRipsWidgets,
      jackpotWidgets,
      slotBattleWidgets,
    ],
  );

  // Sorted before layout so widgets hidden by `home_widgets_order` (null) don't take up layout slots.
  const sortedWidgets: Widget[] = useMemo(() => {
    const unsorted: Widget[] = widgetsData.map((widgetData) => ({
      id: widgetData.id,
      sizeType: 'small' as WidgetSizeType,
      type: widgetData.type,
      data: widgetData.data,
    }));

    return sortWidgets(unsorted, homeWidgetsOrder);
  }, [homeWidgetsOrder, widgetsData]);

  const widgetsCount = sortedWidgets.length;

  const { containerRef, widgetSizeTypes, allWidgetsVisible } = useWidgetLayout({ widgetsCount });

  const widgets: Widget[] = useMemo(
    () =>
      sortedWidgets.map((widget, i) => ({
        ...widget,
        sizeType: widgetSizeTypes[i] ?? ('small' as WidgetSizeType),
      })),
    [sortedWidgets, widgetSizeTypes],
  );

  return { widgets, containerRef, allWidgetsVisible };
}
